import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './MarkdownMessage.css';

/**
 * MarkdownMessage
 *
 * Renders assistant output as formatted markdown instead of a plain-text
 * chat bubble — headings, bold key figures, lists, tables, code, and
 * blockquotes all get real structure, so a response reads like a
 * purpose-built report rather than a text message. Styling leans on the
 * app's black/white/gold palette: bold text and headings pick up gold,
 * everything else stays neutral so the accent actually means something.
 *
 * `code`/`pre` are styled structurally (pre > code vs. bare code) rather
 * than relying on react-markdown's `inline` prop, which isn't consistently
 * available across react-markdown versions.
 */
const components = {
  h1: ({ node, ...props }) => <h1 className="md-h1" {...props} />,
  h2: ({ node, ...props }) => <h2 className="md-h2" {...props} />,
  h3: ({ node, ...props }) => <h3 className="md-h3" {...props} />,
  h4: ({ node, ...props }) => <h4 className="md-h4" {...props} />,
  p: ({ node, ...props }) => <p className="md-p" {...props} />,
  strong: ({ node, ...props }) => <strong className="md-strong" {...props} />,
  em: ({ node, ...props }) => <em className="md-em" {...props} />,
  ul: ({ node, ...props }) => <ul className="md-ul" {...props} />,
  ol: ({ node, ...props }) => <ol className="md-ol" {...props} />,
  li: ({ node, ...props }) => <li className="md-li" {...props} />,
  blockquote: ({ node, ...props }) => <blockquote className="md-blockquote" {...props} />,
  hr: ({ node, ...props }) => <hr className="md-hr" {...props} />,
  a: ({ node, ...props }) => <a className="md-link" target="_blank" rel="noopener noreferrer" {...props} />,
  pre: ({ node, ...props }) => <pre className="md-pre" {...props} />,
  code: ({ node, className, ...props }) => <code className={`md-code${className ? ` ${className}` : ''}`} {...props} />,
  table: ({ node, ...props }) => <div className="md-table-wrap"><table className="md-table" {...props} /></div>,
  thead: ({ node, ...props }) => <thead className="md-thead" {...props} />,
  th: ({ node, ...props }) => <th className="md-th" {...props} />,
  td: ({ node, ...props }) => <td className="md-td" {...props} />,
};

const MarkdownMessage = ({ content }) => (
  <div className="md-content">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  </div>
);

export default MarkdownMessage;
